/**
 * Sprint-6 C.1: Multi-Invoice-Template-Architektur. Tenant kann mehrere Templates pflegen,
 * pro Property optional zuweisen, Primary als Fallback.
 */
export interface InvoiceTemplateResponse {
  id: string;
  name: string;
  isPrimary: boolean;
  companyName: string | null;
  billingStreet: string | null;
  billingPostalCode: string | null;
  billingCity: string | null;
  billingCountry: string | null;
  taxNumber: string | null;
  vatId: string | null;
  iban: string | null;
  bic: string | null;
  bankName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  /**
   * Zahlungsziel in Tagen für manuelle Rechnungen. `null` = keines hinterlegt, der Aussteller
   * entscheidet je Rechnung. OTA-Rechnungen tragen nie eines — dort hat der Kanal kassiert.
   */
  paymentTermDays: number | null;
  hasLogo: boolean;
  createdAt: string;
  updatedAt: string | null;
  /** Was automatischen Rechnungen mit dieser Vorlage fehlt; leer heißt: sie dürfen ausgestellt werden. */
  missingIssuerFields: InvoiceIssuerField[];
}

/** `InvoiceIssuerField` — Pflichtangaben zum Aussteller (§14 Abs. 4 UStG). */
export type InvoiceIssuerField =
  | 'CompanyName'
  | 'BillingStreet'
  | 'BillingPostalCode'
  | 'BillingCity'
  | 'BillingCountry'
  | 'TaxNumberOrVatId';

export interface CreateInvoiceTemplateRequest {
  name: string;
  isPrimary: boolean;
  companyName?: string | null;
  billingStreet?: string | null;
  billingPostalCode?: string | null;
  billingCity?: string | null;
  billingCountry?: string | null;
  taxNumber?: string | null;
  vatId?: string | null;
  iban?: string | null;
  bic?: string | null;
  bankName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  paymentTermDays?: number | null;
}

export interface UpdateInvoiceTemplateRequest {
  name: string;
  companyName?: string | null;
  billingStreet?: string | null;
  billingPostalCode?: string | null;
  billingCity?: string | null;
  billingCountry?: string | null;
  taxNumber?: string | null;
  vatId?: string | null;
  iban?: string | null;
  bic?: string | null;
  bankName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  paymentTermDays?: number | null;
}

export interface AssignInvoiceTemplateRequest {
  templateId: string | null;
}

/**
 * Sprint 7 Issue 4: Tenant-weite Steuer-Saetze. Loest die alten TenantSettings ab —
 * Absender-Daten leben jetzt in InvoiceTemplate.
 */
export interface TaxSettingsResponse {
  taxRateAccommodation: number;
  taxRateServices: number;
}

export interface UpdateTaxSettingsRequest {
  taxRateAccommodation: number;
  taxRateServices: number;
}

export interface PnLResponse {
  /** Year-Approximation = periodFrom.Year — bleibt fuer Backward-Compat. */
  year: number;
  /** ISO YYYY-MM-DD. Bei Year-Filter = "2026-01-01". */
  periodFrom: string;
  /** ISO YYYY-MM-DD. Bei Year-Filter = "2026-12-31". */
  periodTo: string;
  propertyId: string;
  currency: string;
  income: {
    total: number;
    byMonth: Record<string, number>;
    /** Echte Schadensersatzforderungen, getrennt vom Leistungsumsatz. */
    otherIncome: number;
    otherIncomeByMonth: Record<string, number>;
  };
  expenses: {
    /** Variable + Recurring kombiniert. */
    total: number;
    /** Variable + Recurring kombiniert pro Monat. */
    byMonth: Record<string, number>;
    /** Nur variable Belege (SupplierInvoices / PropertyFile-Rechnungen). */
    byCategory: Record<string, number>;
    /** Nur variable Ausgaben (Belege). */
    variableTotal: number;
    /** Nur Recurring nach Anti-Double-Count (Vertraege). */
    recurringTotal: number;
    /** Audit-Spur Recurring fuer Drilldown-Tabelle. */
    recurring: RecurringCostResult;
  };
  commission: {
    total: number;
    byOta: Record<string, number>;
  };
  netResult: number;
}

export interface RecurringCostResult {
  total: number;
  byMonth: Record<string, number>;
  lines: RecurringCostLine[];
}

export interface RecurringCostLine {
  contractId: string | null;
  type: string;
  counterparty: string;
  /** ISO YYYY-MM-DD, immer 1. des Monats. */
  month: string;
  amount: number;
  replacedByInvoice: boolean;
  replacingInvoiceId: string | null;
}

export interface ExpenseListItem {
  fileId: string;
  issuedOn: string;
  counterparty: string | null;
  amount: number;
  currency: string;
  category: string | null;
  kind: string;
}

export interface InvoiceResponse {
  canAccessDocuments?: boolean;
  id: string;
  /** Alle drei `null`, solange Entwurf — sie entstehen erst beim Festschreiben. */
  propertyFileId: string | null;
  invoiceNumber: string | null;
  issuedOn: string | null;
  bookingId: string;
  propertyId: string;
  totalAmount: number;
  currency: string;
  fileName: string;
  createdAt: string;
  isCancelled: boolean;
  cancellationReason: string | null;
  cancelledAt: string | null;
  cancellationInvoice: InvoiceResponse | null;
  /** Zahlbar bis. Leer, wo nichts offen ist — bei OTA hat der Kanal kassiert. */
  dueDate: string | null;
  /**
   * Ohne diese Angabe sieht eine Buchung mit zwei Rechnungen aus wie eine mit einer doppelten —
   * und beim Stornieren wäre nicht erkennbar, welche man gerade in der Hand hat.
   */
  kind: InvoiceKind;
  paidAt?: string | null;
  guestSettlementChannel?: string | null;
  rootInvoiceId?: string | null;
  previousDocumentId?: string | null;
  correctionSequence?: number | null;
  correctionKind?:
    | 'PartialAdjustment'
    | 'FullReversal'
    | 'AmountReplacement'
    | 'RecipientReplacement'
    | null;
  financialDeltaAmount?: number | null;
  corrections?: InvoiceResponse[];
  status?: InvoiceListStatus;
  paidOn?: string | null;
  chainRootId?: string | null;
}

/** A new legal recipient snapshot for one issued invoice; never mutates the guest profile. */
export interface InvoiceRecipientReplacementRequest {
  name: string;
  company: string | null;
  street: string;
  postalCode: string;
  city: string;
  countryCode: string;
  vatId: string | null;
  registryNumber: string | null;
}

export type OtaBookingRefundKind = 'Refund' | 'Compensation';
export type OtaBookingRefundCategory = 'GuestComplaint' | 'Defect';

export interface OtaBookingRefundLedgerItem {
  id: string;
  amountMinor: number;
  currency: string;
  kind: OtaBookingRefundKind;
  category: OtaBookingRefundCategory;
  createdAt: string;
  compensatesRefundId: string | null;
  isCompensated: boolean;
}

export interface OtaBookingRefundLedger {
  effectiveRefundedMinor: number;
  remainingGrossMinor: number;
  currency: string;
  expectedVersion: string;
  items: OtaBookingRefundLedgerItem[];
}

export interface CreateOtaBookingRefundRequest {
  operationId: string;
  amountMinor: number;
  currency: string;
  category: OtaBookingRefundCategory;
  reason: string;
  expectedVersion: string;
}

export interface CompensateOtaBookingRefundRequest {
  operationId: string;
  reason: string;
  expectedVersion: string;
}

export interface OtaBookingRefundResponse {
  id: string;
  effectiveRefundedMinor: number;
  remainingGrossMinor: number;
  currency: string;
  sourceRevisionId: string;
  expectedVersion: string;
}

export type InvoiceKind = 'Accommodation' | 'Parking' | 'Manual' | 'DamageClaim' | 'Service';

export const invoiceKindLabels: Record<InvoiceKind, string> = {
  Accommodation: 'Beherbergung',
  Parking: 'Parkplatz',
  Manual: 'Manuell',
  DamageClaim: 'Schadensersatzforderung',
  Service: 'Zusatzleistung',
};

/**
 * Abgeleiteter Zustand einer Rechnung — nicht gespeichert, sondern aus PaidAt, CancelledAt und
 * ReferencedInvoiceId berechnet. Reihenfolge zählt: eine Stornorechnung ist zuerst eine
 * Stornorechnung, eine stornierte Rechnung zuerst storniert.
 */
export type InvoiceListStatus =
  | 'Draft'
  | 'Open'
  | 'Paid'
  | 'Cancelled'
  | 'CreditNote'
  | 'Correction';

/**
 * Eine Zeile im Rechnungs-Reiter. `storageFolder` ist Absicht: die Übersicht ist eine globale
 * Sicht, kein globaler Speicherort — jede PDF liegt in der Akte ihres Apartments, und die Zeile
 * sagt, wo.
 */
export interface InvoiceListItem {
  canAccessDocuments?: boolean;
  id: string;
  /** `null` solange Entwurf — die Nummer entsteht erst beim Festschreiben. */
  invoiceNumber: string | null;
  issuedOn: string | null;
  /** Ein Entwurf hat keine Datei zum Herunterladen, aber eine Buchung mit seinem Builder. */
  bookingId: string;
  propertyId: string;
  propertyName: string;
  recipient: string | null;
  totalAmount: number;
  currency: string;
  status: InvoiceListStatus;
  paidAt: string | null;
  guestSettlementChannel?: string | null;
  storageFolder: string;
  fileName: string;
  kind: InvoiceKind;
  chainRootId: string;
}

export interface InvoiceListQuery {
  propertyId?: string;
  from?: string;
  to?: string;
  status?: InvoiceListStatus;
  limit?: number;
}

export type ExpenseCategory =
  | 'Strom'
  | 'Gas'
  | 'Wasser'
  | 'Heizoel'
  | 'Internet'
  | 'Muell'
  | 'Versicherung'
  | 'Reparatur'
  | 'Reinigung'
  | 'Verwaltung'
  | 'GrundsteuerKommunal'
  | 'Zinsen'
  | 'Sonstiges';
